import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark } from "@fortawesome/free-solid-svg-icons";
import { displayToast } from "src/shared/helpers/toaster.helper";
import NewModalComponent from "src/core/components/modal/new-modal.component";
import zones_icon from "src/assets/images/zones-icon.png";
import { LocationTree } from "./location-tree.component";
import { AssetPicker } from "./asset-picker.component";
import {
  saveGatewayZone,
  updateGatewayZone,
  saveCommandToGatewayByAssetCode,
  getAssetsByZone,
} from "src/core/api/services/command-module/zone.service";
import { useParams } from "react-router-dom";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { ZoneTypeId } from "src/core/enums/zone-enums";
import type { Zone, ZoneAsset } from "../../components/types";

type ZoneFormModalProps = {
  showModal: boolean;
  handleClose: () => void;
  zone: Zone | null;
  gatewayCode: string;
  onSaved: () => void;
  initialAssignedAssets?: ZoneAsset[];
};

export function ZoneFormModal({
  showModal,
  handleClose,
  zone,
  gatewayCode,
  onSaved,
  initialAssignedAssets,
}: ZoneFormModalProps) {
  const { id } = useParams<{ id: string }>();
  const assetCode = id ? safeAtob(id) : null;

  const [zoneName, setZoneName] = useState("");
  const [selectedBlockCode, setSelectedBlockCode] = useState<string | undefined>();
  const [selectedFloorCode, setSelectedFloorCode] = useState<string | undefined>();
  const [assignedAssets, setAssignedAssets] = useState<ZoneAsset[]>([]);
  const [isLoadingZoneAssets, setIsLoadingZoneAssets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!zone;

  useEffect(() => {
    if (showModal) {
      if (zone) {
        setZoneName(zone.name);
        setSelectedBlockCode(zone.blockCode);
        setSelectedFloorCode(zone.floorCode);
        if (initialAssignedAssets) {
          setAssignedAssets(initialAssignedAssets);
          setIsLoadingZoneAssets(false);
          return;
        }

        setAssignedAssets([]);
        setIsLoadingZoneAssets(true);
        getAssetsByZone(zone.id)
          .then((res) => {
            const items = (res?.data || []).map((item: any) => ({
              assetId: item.assetId ?? item.id,
              assetCode: item.assetCode,
              assetName: item.sensorDisplayName || item.assetName || `Asset ${item.assetCode}`,
              assetType: item.assetType,
              blockName: item.blockName,
              street: item.street,
              zoneTypeId: ZoneTypeId.LightNode,
            }));
            setAssignedAssets(items);
          })
          .catch((error) => {
            console.error("Failed to load zone assets: ", error);
          })
          .finally(() => {
            setIsLoadingZoneAssets(false);
          });
      } else {
        setZoneName("");
        setSelectedBlockCode(undefined);
        setSelectedFloorCode(undefined);
        setAssignedAssets(initialAssignedAssets || []);
        setIsLoadingZoneAssets(false);
      }
    }
  }, [showModal, zone, initialAssignedAssets]);

  const handleSave = async () => {
    if (!zoneName.trim()) {
      displayToast("Zone name is required.", "red");
      return;
    }

    if (!assetCode) {
      displayToast("Asset code is missing.", "red");
      return;
    }

    const assetsToSave = assignedAssets.filter((asset) => !asset.isRemove);

    if (assetsToSave.length === 0) {
      displayToast("Select at least one asset for this zone.", "red");
      return;
    }

    setIsSubmitting(true);

    try {
      const removedAssetCodes = assignedAssets
        .filter((asset) => asset.isRemove)
        .map((asset) => asset.assetCode)
        .filter(Boolean);
      const gatewayZoneDetails = assetsToSave.map((a) => ({
        zoneTypeId: ZoneTypeId.LightNode,
        assetCode: a.assetCode,
      }));

      if (isEditMode && zone) {
        const payload = {
          id: zone.id,
          zoneName: zoneName.trim(),
          gatewayCode: assetCode,
          gatewayZoneDetails,
        };
        const response = await updateGatewayZone(payload);
        if (response?.status == 200 && removedAssetCodes.length > 0) {
          await saveCommandToGatewayByAssetCode(removedAssetCodes);
        }
        // displayToast("Zone updated successfully.", "green");
      } else {
        const payload = {
          zoneName: zoneName.trim(),
          gatewayCode: assetCode,
          gatewayZoneDetails,
        };
        await saveGatewayZone(payload);
        // displayToast("Zone created successfully.", "green");
      }

      onSaved();
      handleClose();
    } catch (error: any) {
      console.error("Failed to save zone: ", error);
      displayToast(
        error?.response?.data?.message || "Failed to save zone.",
        "red"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectBlock = (blockCode: string, floorCode?: string) => {
    setSelectedBlockCode(blockCode);
    setSelectedFloorCode(floorCode);
  };

  const handleAssignAssets = (assets: ZoneAsset[]) => {
    setAssignedAssets(assets);
  };

  const modalActionConfig = [
    {
      label: isSubmitting ? "Saving..." : "Save Zone",
      event: handleSave,
      disabled: isSubmitting,
    },
  ];

  return (
    <NewModalComponent
      showModal={showModal}
      handleClose={handleClose}
      headerTitle={
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <img
            src={zones_icon}
            alt="Zone"
            style={{ width: 20, height: 20 }}
          />
          <span>{isEditMode ? "Edit Zone" : "Add New Zone"}</span>
        </div>
      }
      actionConfig={modalActionConfig}
      size="xl"
      footerAlign="flex-end"
    >
      <div className="zone-form-modal">
        <div className="zone-form">
          <div className="zone-form__section">
            <label className="zone-form__label">Zone Name</label>
            <div className="zone-form__input-wrap">
              <FontAwesomeIcon icon={faBookmark} className="zone-form__input-icon" />
              <input
                type="text"
                className="zone-form__input"
                placeholder="e.g. North Corridor Section B"
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
              />
            </div>
          </div>

          <div className="zone-form__section">
            <label className="zone-form__label">Location / Floor Plan</label>
            <LocationTree
              gatewayCode={assetCode || gatewayCode}
              zone={zone}
              assignedAssets={assignedAssets}
              selectedBlockCode={selectedBlockCode}
              selectedFloorCode={selectedFloorCode}
              onSelectBlock={handleSelectBlock}
            />
          </div>

          <div className="zone-form__info">
            <i className="bi bi-info-circle" />
            <span>Restricting asset selection to current gateway ({gatewayCode || "current gateway"}).</span>
          </div>

          <div className="zone-form__section">
            <AssetPicker
              gatewayCode={assetCode || gatewayCode || ""}
              assignedAssets={assignedAssets}
              onAssign={handleAssignAssets}
              zone={zone}
              isEditMode={isEditMode}
              isLoading={isLoadingZoneAssets}
            />
          </div>
        </div>
      </div>
    </NewModalComponent>
  );
}
